import React, { useEffect, useRef, useState } from 'react';
// Local
import { ReplacementToken, tokenReplace } from 'lib/utils/string-utils';
import svgStyles from 'components/helpers/SvgIcon/SvgIcons.module.scss';
import onetrustPlaceholderStyles from 'components/helpers/OneTrust/OneTrustPlaceholder/OneTrustPlaceholder.module.scss';
import ctaStyles from 'components/helpers/Atoms/Cta/Cta.module.scss';
import clsx from 'clsx';
import { getDownloadUrl, isAsDownload } from 'lib/utils/download-utils';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
import { getLocaleUrl } from 'lib/utils/domain-locale-utils';
import useDictionary from 'lib/hooks/useDictionary';
import {
  OneTrustDefaultConsentPreference,
  OneTrustGroup,
  useOneTrustConsent,
} from 'lib/hooks/useOneTrustConsent';

export interface EnhancedRichTextOptionalProps {
  maxLines?: 1 | 2 | 3 | 4 | 5 | 6;
  maxCharacters?: number;
  listColumns?: 2 | 3 | 4;
}

interface EnhancedRichTextProps extends React.HTMLAttributes<HTMLElement> {
  value: string;
  tokens?: ReplacementToken[];
  parentElementType?: keyof JSX.IntrinsicElements;
  options?: EnhancedRichTextOptionalProps;
}

const EnhancedRichText = (props: EnhancedRichTextProps): JSX.Element => {
  const { value, tokens, className, parentElementType = 'div', options, ...otherProps } = props;

  // Cast to React.ElementType to avoid exhaustive JSX intrinsic element checking
  const Tag = parentElementType as React.ElementType;

  const updatedValue = useEnhancedRichText({ value, tokens, options });
  if (!updatedValue) return <></>;
  return (
    <Tag
      {...otherProps}
      className={getRteClassNames(className, options)}
      data-component="helpers/atoms/richtext"
      style={{ ...otherProps.style }}
      dangerouslySetInnerHTML={{ __html: updatedValue }}
    />
  );
};

export function getRteClassNames(
  className: string | undefined,
  options: EnhancedRichTextOptionalProps | undefined
) {
  const { maxLines, listColumns } = options || {};
  return clsx(
    'rte',
    className,
    listColumns ? `list-columns-${listColumns}` : undefined,
    maxLines ? `line-clamp-${maxLines}` : undefined
  );
}
const NEW_TAB_ICON_STRING = `<span class="${svgStyles.svgIcon} ${svgStyles.svgIconLeftMargin} ${svgStyles.em}" style="text-decoration:none;display:inline-block">
  <svg aria-hidden="true" class="inline ml-2 -mt-1 h-em w-em" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M18.2222 18.2222H5.77778V5.77778H12V4H5.77778C4.79111 4 4 4.8 4 5.77778V18.2222C4 19.2 4.79111 20 5.77778 20H18.2222C19.2 20 20 19.2 20 18.2222V12H18.2222V18.2222ZM13.6556 4V5.81269H16.9094L8 14.7221L9.27795 16L18.1873 7.09063V10.3444H20V4H13.6556Z" fill="currentColor"/>
  </svg>
</span>`;
const DOWNLOAD_ICON_STRING = `<span class="${svgStyles.svgIcon} ${svgStyles.svgIconLeftMargin} ${svgStyles.em}" style="text-decoration:none;display:inline-block">
  <svg aria-hidden="true" class="inline ml-2 -mt-1 h-em w-em" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 0C17.0751 0 22 4.92487 22 11C22 17.0751 17.0751 22 11 22C4.92487 22 0 17.0751 0 11C0 4.92487 4.92487 0 11 0ZM11 2C6.02944 2 2 6.02944 2 11C2 15.9706 6.02944 20 11 20C15.9706 20 20 15.9706 20 11C20 6.02944 15.9706 2 11 2ZM11 6C11.5523 6 12 6.44772 12 7V12.5859L14.293 10.293C14.6835 9.90244 15.3165 9.90244 15.707 10.293C16.0976 10.6835 16.0976 11.3165 15.707 11.707L11.707 15.707C11.6592 15.7548 11.6065 15.7976 11.5498 15.835C11.5121 15.8599 11.4727 15.8811 11.4326 15.9004C11.3874 15.9222 11.3407 15.9412 11.292 15.9561C11.2717 15.9622 11.251 15.9668 11.2305 15.9717C11.2191 15.9744 11.2078 15.9772 11.1963 15.9795C11.1859 15.9816 11.1754 15.9826 11.165 15.9844C11.1112 15.9933 11.0563 16 11 16C10.9433 16 10.8881 15.9934 10.834 15.9844C10.8236 15.9826 10.8131 15.9816 10.8027 15.9795C10.7912 15.9772 10.7799 15.9744 10.7686 15.9717C10.748 15.9668 10.7273 15.9623 10.707 15.9561C10.6584 15.9412 10.6116 15.9222 10.5664 15.9004C10.5266 15.8812 10.4877 15.8597 10.4502 15.835C10.3935 15.7976 10.3408 15.7548 10.293 15.707L6.29297 11.707C5.90244 11.3165 5.90244 10.6835 6.29297 10.293C6.68349 9.90244 7.31651 9.90244 7.70703 10.293L10 12.5859V7C10 6.44772 10.4477 6 11 6Z" />
  </svg>
</span>`;
const NON_NAV_PROTOCOLS = new Set(['mailto:', 'tel:', 'data:', 'about:']);

// Consent groups required before an embedded video may load — matches the standalone
// EmbeddedVideo component so authored (raw HTML) videos are gated the same way.
const RICH_TEXT_VIDEO_OPTANON_GROUPS: OneTrustGroup[] = ['2', '3', '4'];
const VIDEO_IFRAME_HOST_PATTERN =
  /(^|\.)(youtube\.com|youtube-nocookie\.com|youtu\.be|vimeo\.com|tiktok\.com|instagram\.com)$/i;
// Cheap check on the raw markup so OneTrust consent evaluation (cookie reads,
// polling) only runs for rich text that actually embeds a video.
const RAW_VIDEO_IFRAME_HINT =
  /<iframe[^>]+src=["'][^"']*(youtube|youtu\.be|vimeo|tiktok|instagram)/i;

function isVideoIframeSrc(src: string): boolean {
  try {
    const url = new URL(src, 'https://placeholder.invalid');
    return VIDEO_IFRAME_HOST_PATTERN.test(url.hostname);
  } catch {
    return false;
  }
}

function buildVideoConsentPlaceholder(
  getDictionaryValue: (key: string, fallback?: string) => string,
  copyContainerId: string
): HTMLDivElement {
  const placeholder = document.createElement('div');
  placeholder.className = onetrustPlaceholderStyles.consentPlaceholder;
  placeholder.setAttribute('data-component', 'helpers/atoms/richtext/video-consent-placeholder');

  const copyContainer = document.createElement('div');
  copyContainer.className = onetrustPlaceholderStyles.copyContainer;
  copyContainer.id = copyContainerId;

  const headline = getDictionaryValue('Common.OneTrustSettings.VideoConsentHeadline');
  if (headline) {
    const heading = document.createElement('div');
    heading.className = onetrustPlaceholderStyles.consentHeading;
    heading.textContent = headline;
    copyContainer.appendChild(heading);
  }

  const body = document.createElement('p');
  body.className = onetrustPlaceholderStyles.consentBody;
  body.textContent = getDictionaryValue('Common.OneTrustSettings.ConsentBody');
  copyContainer.appendChild(body);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = clsx(
    ctaStyles.cta,
    onetrustPlaceholderStyles.consentButton,
    ctaStyles.brand,
    ctaStyles.outline,
    ctaStyles.base
  );
  button.setAttribute('data-gmiot', 'gmi-onetrust-preference-link');
  button.setAttribute('aria-describedby', copyContainerId);

  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('class', clsx(svgStyles.svgIcon, svgStyles.sm));
  icon.setAttribute('fill', 'currentColor');
  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('data-icon', 'leading');
  icon.setAttribute('aria-hidden', 'true');
  const iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  iconPath.setAttribute('fill-rule', 'evenodd');
  iconPath.setAttribute('clip-rule', 'evenodd');
  iconPath.setAttribute(
    'd',
    'M15 13C13.6937 13 12.5825 13.8349 12.1707 15.0001L4 15V17L12.171 17.0009C12.5831 18.1656 13.6941 19 15 19C16.3059 19 17.4169 18.1656 17.829 17.0009L20 17V15L17.8293 15.0001C17.4175 13.8349 16.3063 13 15 13ZM15 15C15.5523 15 16 15.4477 16 16C16 16.5128 15.614 16.9355 15.1166 16.9933L15 17C14.4477 17 14 16.5523 14 16C14 15.4872 14.386 15.0645 14.8834 15.0067L15 15ZM9 5C10.3063 5 11.4175 5.83485 11.8293 7.00009L20 7V9L11.829 9.0009C11.4169 10.1656 10.3059 11 9 11C7.69412 11 6.58311 10.1656 6.17102 9.0009L4 9V7L6.17067 7.00009C6.58249 5.83485 7.69375 5 9 5ZM9 7C8.44772 7 8 7.44772 8 8C8 8.51284 8.38604 8.93551 8.88338 8.99327L9 9C9.55228 9 10 8.55228 10 8C10 7.48716 9.61396 7.06449 9.11662 7.00673L9 7Z'
  );
  iconPath.setAttribute('fill', 'currentColor');
  icon.appendChild(iconPath);

  const label = document.createElement('span');
  label.textContent = getDictionaryValue('Common.OneTrustSettings.CookieSettingLabel');

  button.appendChild(icon);
  button.appendChild(label);
  button.addEventListener('click', () => {
    if (typeof window !== 'undefined' && window.OneTrust) {
      window.OneTrust.ToggleInfoDisplay();
    }
  });

  placeholder.appendChild(copyContainer);
  placeholder.appendChild(button);
  return placeholder;
}

function isRootRelativeLink(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

function normalizeInternalHref(href: string, siteName?: string, locale?: string): string {
  const trimmed = href.trim();
  if (!trimmed || !siteName || !locale || !isRootRelativeLink(trimmed)) {
    return href;
  }

  if (trimmed.startsWith('/api/')) {
    return href;
  }

  try {
    return getLocaleUrl(siteName, locale, trimmed, true);
  } catch {
    return href;
  }
}

function isExternalLink(href: string): boolean {
  try {
    const trimmed = (href || '').trim();
    if (!trimmed || trimmed === '#' || /^javascript:/i.test(trimmed)) return false;
    if (typeof window === 'undefined') return false;
    const base = window.location;
    const url = new URL(trimmed, base.origin);
    if (NON_NAV_PROTOCOLS.has(url.protocol)) return false;
    return url.hostname !== base.hostname;
  } catch {
    return false;
  }
}
function mergeRel(existingRel: string | null | undefined, additions: string[]): string {
  const set = new Set<string>((existingRel || '').split(/\s+/).filter(Boolean));
  additions.forEach((a) => set.add(a));
  return Array.from(set).join(' ');
}
function appendOnce(el: Element, selector: string, html: string) {
  if (!el.querySelector(selector)) el.insertAdjacentHTML('beforeend', html);
}

type UseEnhancedRichTextArgs = Pick<EnhancedRichTextProps, 'value' | 'tokens'> & {
  options?: EnhancedRichTextOptionalProps;
};

function truncateHtmlContentByTextLength(root: ParentNode, maxCharacters: number): boolean {
  const effectiveLimit = Math.min(260, Math.max(0, Math.floor(Number(maxCharacters) || 0)));

  let remaining = effectiveLimit;
  let didTruncate = false;

  const truncateNode = (node: Node) => {
    if (remaining <= 0) {
      node.parentNode?.removeChild(node);
      didTruncate = true;
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (text.length <= remaining) {
        remaining -= text.length;
        return;
      }

      node.textContent = text.slice(0, remaining);
      remaining = 0;
      didTruncate = true;
      return;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      if (element.tagName.toLowerCase() === 'img') {
        return;
      }

      const children = Array.from(node.childNodes);
      for (const child of children) {
        truncateNode(child);
      }

      if (!node.hasChildNodes()) {
        node.parentNode?.removeChild(node);
      }
    }
  };

  const children = Array.from(root.childNodes);
  for (const child of children) {
    truncateNode(child);
  }

  if (!didTruncate) {
    return false;
  }

  return true;
}

export function useEnhancedRichText({ value, tokens, options }: UseEnhancedRichTextArgs) {
  const { page } = useSitecore();
  const isGMFSSite = page?.layout?.sitecore?.context?.site?.name === 'GMFS';
  const siteName = page?.layout?.sitecore?.context?.site?.name;
  const locale = page?.layout?.sitecore?.context?.language || page?.locale;
  const richTextContent = tokens ? tokenReplace(value, tokens) : value;
  const [content, setContent] = useState(richTextContent);
  const updatedField = content || '';
  const videoConsentIdPrefix = useRef(
    `richtext-video-consent-${Math.random().toString(36).slice(2)}`
  ).current;
  const { getDictionaryValue } = useDictionary();
  const hasVideoIframeHint = RAW_VIDEO_IFRAME_HINT.test(richTextContent || '');
  const { isBlocked: isVideoConsentBlocked, readyToMount: isVideoConsentReady } =
    useOneTrustConsent(
      RICH_TEXT_VIDEO_OPTANON_GROUPS,
      OneTrustDefaultConsentPreference.NoConsent,
      hasVideoIframeHint
    );
  useEffect(() => {
    const template = document.createElement('template');
    template.innerHTML = richTextContent || '';

    if (options?.maxCharacters) {
      truncateHtmlContentByTextLength(template.content, options.maxCharacters);
    }

    // Strip authored headings with no real accessible name (e.g. a leftover
    // `<h2>&nbsp;</h2>` from pasted content) so screen readers don't announce
    // an empty heading level.
    template.content.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
      if (!(heading.textContent || '').replace(/\s+/g, '')) {
        heading.remove();
      }
    });

    // Rich text body copy should never introduce a second page-level h1 (the
    // page's own hero/title owns that). Demote any authored h1 to h2, keeping
    // its attributes and content intact.
    template.content.querySelectorAll('h1').forEach((heading) => {
      const demoted = document.createElement('h2');
      Array.from(heading.attributes).forEach((attr) => demoted.setAttribute(attr.name, attr.value));
      demoted.append(...Array.from(heading.childNodes));
      heading.replaceWith(demoted);
    });

    const links = Array.from(template.content.querySelectorAll('a'));
    links.forEach((a) => {
      if (a.querySelector('img')) return;
      const href = a.getAttribute('href') || '';
      const normalizedHref = normalizeInternalHref(href, siteName, locale);
      if (normalizedHref !== href) {
        a.setAttribute('href', normalizedHref);
      }
      const isMailtoLink = href.trim().toLowerCase().startsWith('mailto:');
      const title = a.getAttribute('title')?.trim() ?? '';
      const linkText = (a.textContent ?? '').replace(/\s+/g, ' ').trim();
      if (title && linkText && title === linkText) {
        a.removeAttribute('title'); //Removing title attribute if it is the same as link text to avoid redundancy for screen readers
      }
      const external = isExternalLink(normalizedHref);
      if (isGMFSSite && isMailtoLink) {
        a.removeAttribute('download');
        return;
      }
      if (isAsDownload({ href: normalizedHref })) {
        a.setAttribute('download', '');
        a.setAttribute('href', getDownloadUrl(normalizedHref));
        a.removeAttribute('target');
        a.setAttribute('rel', mergeRel(a.getAttribute('rel'), ['noopener', 'noreferrer']));
        appendOnce(
          a,
          'span.screenReaderText',
          `<span class="screenReaderText"> downloads a file</span>`
        );
        appendOnce(
          a,
          `span.${svgStyles.svgIcon}.${svgStyles.svgIconLeftMargin}.${svgStyles.em}`,
          DOWNLOAD_ICON_STRING
        );
        return;
      }
      if (external && !(a.getAttribute('target') || '').trim()) {
        a.setAttribute('target', '_blank');
      }
      const opensInNewTab = (a.getAttribute('target') || '').trim() === '_blank';
      if (!opensInNewTab) return; // ensures target=_self never shows SR text
      a.setAttribute('rel', mergeRel(a.getAttribute('rel'), ['noopener', 'noreferrer']));
      appendOnce(
        a,
        'span.screenReaderText',
        `<span class="screenReaderText"> (Opens in a new tab)</span>`
      );
      if (external) {
        appendOnce(
          a,
          `span.${svgStyles.svgIcon}.${svgStyles.svgIconLeftMargin}.${svgStyles.em}`,
          NEW_TAB_ICON_STRING
        );
      }
    });
    // Table accessibility enhancements
    const tables = template.content.querySelectorAll('table');
    tables.forEach((table) => {
      table.querySelectorAll('thead th').forEach((th) => th.setAttribute('scope', 'col'));
      table.querySelectorAll('tbody th').forEach((th) => th.setAttribute('scope', 'row'));
      const headerElements = table.querySelectorAll('thead th');
      if (headerElements.length > 0) {
        const headers = Array.from(headerElements).map((th) => th.textContent?.trim() || '');
        table.querySelectorAll('tbody td, tbody th').forEach((cell, index) => {
          cell.setAttribute('data-column', headers[index % headers.length]);
        });
      }
    });

    // Authors can paste raw video embed iframes (YouTube/Vimeo/TikTok/Instagram)
    // directly into rich text. Gate them behind the same OneTrust consent groups
    // the standalone EmbeddedVideo component requires, failing closed until
    // consent is confirmed.
    if (hasVideoIframeHint) {
      const videoIframes = Array.from(template.content.querySelectorAll('iframe[src]')).filter(
        (iframe) => isVideoIframeSrc(iframe.getAttribute('src') || '')
      );

      if (!isVideoConsentReady || isVideoConsentBlocked) {
        videoIframes.forEach((iframe, index) => {
          iframe.replaceWith(
            buildVideoConsentPlaceholder(getDictionaryValue, `${videoConsentIdPrefix}-${index}`)
          );
        });
      }
    }

    setContent(template.innerHTML);
  }, [
    locale,
    richTextContent,
    siteName,
    isGMFSSite,
    options?.maxCharacters,
    hasVideoIframeHint,
    isVideoConsentBlocked,
    isVideoConsentReady,
    videoConsentIdPrefix,
    getDictionaryValue,
  ]);
  return updatedField;
}
export default EnhancedRichText;
